import React, { useEffect, useState } from 'react';
import { Table, Input, message, Tag, Button, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getTasksAsync, getTasksAsyncArray, deleteTaskAsync } from 'actions/tasks-actions';
import { getJobsAsync, getJobsAsyncArray } from 'actions/jobs-actions';
import { getProjectsAsync, getProjectsAsyncArray } from 'actions/projects-actions';
import { CombinedState } from 'reducers';
import { getCore } from 'cvat-core-wrapper';
import { resetErrors as resetNotificationsErrors } from 'actions/notification-actions';
import ExportDatasetModal from 'components/export-dataset/export-dataset-modal';
import { exportActions } from 'actions/export-actions';
import { DownloadOutlined } from '@ant-design/icons';
import Modal from 'antd/lib/modal';
import { Spin } from 'antd';
import './quality-assessment-page.css';

const formatFR = (d: string | Date): string => {
    if (!d || d === 'N/A') return 'N/A';
    return new Date(d)
        .toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
        })
        .replace(',', '');
};

type PaginatedResponse<T> = {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
};

async function fetchAllPages<T>(firstUrl: string, maxPages = 200): Promise<T[]> {
    const out: T[] = [];
    let url: string | null = firstUrl;
    let pages = 0;

    while (url) {
        pages++;
        if (pages > maxPages) throw new Error(`Too many pages (>${maxPages}) for ${firstUrl}`);

        const resp = await fetch(url, { credentials: 'include' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status} while fetching ${url}`);

        const json = (await resp.json()) as PaginatedResponse<T>;
        out.push(...(json.results || []));
        url = json.next;
    }

    return out;
}

async function mapWithConcurrency<T, R>(
    items: readonly T[],
    limit: number,
    mapper: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
    if (limit <= 0) throw new Error('limit must be > 0');

    const results = new Array<R>(items.length);
    let nextIndex = 0;

    async function worker(): Promise<void> {
        while (true) {
            const i = nextIndex++;
            if (i >= items.length) return;
            results[i] = await mapper(items[i], i);
        }
    }

    const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker());
    await Promise.all(workers);

    return results;
}


interface DataType {
    key: string;
    taskId: number;
    datasetName: string;
    batchName: string;
    state: string;
    stage: string;
    issueCount: number;
    classificationCount: number;
    omissionCount: number;
    outliningCount: number;
    extraAnnotationCount: number;
    otherIssuesCount: number;
    totalObjects: number;
    percentageIssues: number;
    numImages: number;
    annotationCreatedDate: string;
    lastAnnotationUpdatedDate: string;
    qcJobCount: number;
    comment?: string;
    generalComments?: string;
}

type QualityAssessmentEntry = {
    id: number;
    dataset_name: string;
    annotation_created_date: string;
    comment?: string | null;
    general_comments?: string | null;
};

export default function QualityAssessmentPage(): JSX.Element {
    const dispatch = useDispatch();
    const tasks = useSelector((state: CombinedState) => state.tasks.current);
    const jobs = useSelector((state: CombinedState) => state.jobs.current);
    const projects = useSelector((state: CombinedState) => state.projects.current);
    const fetchingTasks = useSelector((state: CombinedState) => state.tasks.fetching);
    const fetchingJobs = useSelector((state: CombinedState) => state.jobs.fetching);
    const fetchingProjects = useSelector((state: CombinedState) => state.projects.fetching);
    const [fullTasks, setFullTasks] = useState<any[]>([]);
    const [fullJobs, setFullJobs] = useState<any[]>([]);
    const [fullProjects, setFullProjects] = useState<any[]>([]);

    const [data, setData] = useState<DataType[]>([]);
    // Comments keyed by taskId (so we can disambiguate same-name datasets) temporary frontend-only fix todo: implement proper backend support
    const [comments, setComments] = useState<Record<number, string>>({});
    // --- signature helpers
    const SIG = '__qa_meta__';
    const makeSig = (taskId: number) => `${SIG}[taskId=${taskId}] `;
    const parseSig = (raw?: string): { owner?: number; text: string } => {
        const s = raw || '';
        const m = s.match(/^__qa_meta__\[taskId=(\d+)\]\s*/);
        if (!m) return { text: s };
        return { owner: Number(m[1]), text: s.slice(m[0].length) };
    };
    const [loading, setLoading] = useState(true);
    const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
    const [selectedDataset, setSelectedDataset] = useState<string | null>(null);

    const core = getCore();

    // Save the entire table when a comment is changed
    const getCSRFToken = (): string | null => {
        return (
            document.cookie
                .split('; ')
                .find((row) => row.startsWith('csrftoken='))
                ?.split('=')[1] || null
        );
    };

    const exportToCSV = () => {
        if (!data.length) return;

        const headers = [
            'Batch Name',
            'Dataset Name',
            'Number of Images',
            'State',
            'Stage',
            'Issues Found',
            'Incorrect Classification',
            'Omission Issues',
            'Outlining Issues',
            'Extra Annotations',
            'Other Issues',
            'Dataset Objects',
            'Percentage Issues',
            'Annotation Job Created',
            'Last Annotation Updated',
            'Number of iteration',
            'Comments',
            'General Comments Summary',
        ];

        const rows = data.map((row) => {
            // flatten general comments into "frame#comment1|frame#comment2 ..."
            const flatGeneralComments = (row.generalComments || '')
                .split(/\r?\n/)
                .map((s) => s.trim())
                .filter((s) => s.length > 0)
                .join('|');

            return [
                row.batchName,
                row.datasetName,
                row.numImages,
                row.state,
                row.stage,
                row.issueCount,
                row.classificationCount,
                row.omissionCount,
                row.outliningCount,
                row.extraAnnotationCount,
                row.otherIssuesCount,
                row.totalObjects,
                `${row.percentageIssues.toFixed(2)}%`,
                row.annotationCreatedDate,
                row.lastAnnotationUpdatedDate,
                row.qcJobCount,
                comments[(row as any).taskId] || '',
                flatGeneralComments,
            ];
        });

        const csvContent = [
            headers.join(';'),
            ...rows.map((r) => r.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(';')),
        ].join('\n');

        // Add BOM to ensure Excel opens UTF-8 correctly
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'quality_assessment.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const sendAllToCropDataSync = async () => {
        if (!data.length) {
            message.warning('No data available to send.');
            return;
        }

        try {
            message.loading({ content: 'Sending all data to CropDataSync...', key: 'sync', duration: 0 });

            // Build the payload list
            const payload = data.map((row) => ({
                dataset_name: row.datasetName,
                batch_name: row.batchName,
                num_images: row.numImages,
                state: row.state,
                stage: row.stage,
                issue_count: row.issueCount,
                classification_count: row.classificationCount,
                omission_count: row.omissionCount,
                outlining_count: row.outliningCount,
                extra_annotation_count: row.extraAnnotationCount,
                other_issues_count: row.otherIssuesCount,
                total_objects: row.totalObjects,
                percentage_issues: row.percentageIssues,
                annotation_created_date: row.annotationCreatedDate,
                last_annotation_updated_date: row.lastAnnotationUpdatedDate,
                qc_job_count: row.qcJobCount,
                comment: comments[row.taskId] || '',
                general_comments: row.generalComments || '',
            }));

           // Forward to CropDataSync through CVAT backend proxy
            const getCSRFToken = (): string | null => {
                return (
                    document.cookie
                        .split('; ')
                        .find((row) => row.startsWith('csrftoken='))
                        ?.split('=')[1] || null
                );
            };

            const csrfToken = getCSRFToken();

            const response = await fetch('/api/forward_cropdatasync', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken || '',
                },
                credentials: 'include',
                body: JSON.stringify(payload),
            });



            if (!response.ok) {
                const err = await response.text();
                throw new Error(`Server returned ${response.status}: ${err}`);
            }

            message.success({ content: 'All data sent to CropDataSync successfully!', key: 'sync', duration: 3 });
        } catch (error: any) {
            console.error(' Failed to send data to CropDataSync:', error);
            message.error({ content: 'Failed to send all data to CropDataSync.', key: 'sync' });
        } finally {
            message.destroy('sync');
        }
    };


    const saveComment = async (taskId: number, datasetName: string, comment: string) => {
        try {
            const csrfToken = getCSRFToken();
            if (!csrfToken) throw new Error('CSRF token is missing. Ensure you are authenticated.');
            // Find the dataset row to get annotationCreatedDate
            const row = data.find(r => r.taskId === taskId && r.datasetName === datasetName);
            if (!row) throw new Error('Unable to find matching dataset row for saving comment.');

            const annotationCreatedDate = row.annotationCreatedDate;

            // Fetch all quality assessments (paginated) and match with composite key
            const allEntries = await fetchAllPages<QualityAssessmentEntry>(
                '/api/quality_assessment?page=1&page_size=100',
            );

            const existingEntry = allEntries.find(
                (item) =>
                    item.dataset_name === datasetName &&
                    item.annotation_created_date === annotationCreatedDate
            );

            let method = 'POST';
            let url = `/api/quality_assessment`;

            const payload: any = { comment: makeSig(taskId) + comment };

            if (existingEntry) {
                method = 'PATCH';
                url = `/api/quality_assessment/${existingEntry.id}`;
            } else {
                // Create a new row
                Object.assign(payload, {
                    dataset_name: datasetName,
                    batch_name: row.batchName,
                    num_images: row.numImages,
                    state: row.state,
                    stage: row.stage,
                    issue_count: row.issueCount,
                    classification_count: row.classificationCount,
                    omission_count: row.omissionCount,
                    outlining_count: row.outliningCount,
                    extra_annotation_count: row.extraAnnotationCount,
                    other_issues_count: row.otherIssuesCount,
                    total_objects: row.totalObjects,
                    percentage_issues: row.percentageIssues,
                    annotation_created_date: annotationCreatedDate,
                    last_annotation_updated_date: row.lastAnnotationUpdatedDate,
                    qc_job_count: row.qcJobCount,
                    general_comments: row.generalComments || '',
                });
            }

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                credentials: 'include',
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to save comment: ${errorText}`);
            }

            message.success(`Comment for ${datasetName} saved successfully!`);
        } catch (error) {
            console.error(`Error saving comment for ${datasetName}:`, error);
            message.error('Failed to save comment.');
        }
    };


    const handleCommentChange = (taskId: number, value: string) => {
        setComments((prev) => ({
            ...prev,
            [taskId]: value,
        }));
    };

    const handleCommentBlur = (taskId: number, datasetName: string) => {
        const value = comments[taskId];
        if (typeof value === 'string') {
            saveComment(taskId, datasetName, value);
        }
    };

    const handleCommentKeyPress = (
        taskId: number,
        datasetName: string,
        event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleCommentBlur(taskId, datasetName);
        }
    };

    const refreshTable = async () => {
        setLoading(true);
        const allTasks = await fetchAllPaginated(getTasksAsyncArray, 'tasks');
        const allJobs = await fetchAllPaginated(getJobsAsyncArray, 'jobs');
        const allProjects = await fetchAllPaginated(getProjectsAsyncArray, 'projects');

        setFullTasks(allTasks);
        setFullJobs(allJobs);
        setFullProjects(allProjects);
    };

    const saveFullTableToBackend = async (tableData: DataType[]) => {
        if (!tableData.length) return;

        try {
            const csrfToken = getCSRFToken();
            if (!csrfToken) throw new Error('Missing CSRF token');

            message.loading({
                content: 'Saving quality assessment table to database...',
                key: 'qa_save',
                duration: 0,
            });

            // Fetch existing entries to decide between POST / PATCH
            const existingEntries = await fetchAllPages<QualityAssessmentEntry>(
                '/api/quality_assessment?page=1&page_size=100',
            );

            // Build a composite key map using dataset_name + annotation_created_date
            const existingByKey = new Map(
                existingEntries.map((item) => [
                    `${item.dataset_name}::${item.annotation_created_date}`,
                    item,
                ])
            );

            for (const row of tableData) {
                const key = `${row.datasetName}::${row.annotationCreatedDate}`;
                const existingEntry = existingByKey.get(key);

                // Build payload preserving existing comments when not edited
                const payload = {
                    dataset_name: row.datasetName,
                    batch_name: row.batchName,
                    num_images: row.numImages,
                    state: row.state,
                    stage: row.stage,
                    issue_count: row.issueCount,
                    classification_count: row.classificationCount,
                    omission_count: row.omissionCount,
                    outlining_count: row.outliningCount,
                    extra_annotation_count: row.extraAnnotationCount,
                    other_issues_count: row.otherIssuesCount,
                    total_objects: row.totalObjects,
                    percentage_issues: row.percentageIssues,
                    annotation_created_date: row.annotationCreatedDate,
                    last_annotation_updated_date: row.lastAnnotationUpdatedDate,
                    qc_job_count: row.qcJobCount,
                    comment: comments[row.taskId] ?? existingEntry?.comment ?? '',
                    general_comments: row.generalComments || existingEntry?.general_comments || '',
                };

                const method = existingEntry ? 'PATCH' : 'POST';
                const url = existingEntry
                    ? `/api/quality_assessment/${existingEntry.id}`
                    : '/api/quality_assessment';

                await fetch(url, {
                    method,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    credentials: 'include',
                    body: JSON.stringify(payload),
                });
            }

            message.success({
                content: 'Quality assessment table saved successfully!',
                key: 'qa_save',
                duration: 3,
            });
        } catch (error) {
            console.error('Failed to save QC table:', error);
            message.error({
                content: 'Failed to save quality assessment table to backend.',
                key: 'qa_save',
            });
        } finally {
            message.destroy('qa_save');
        }
    };



    async function fetchAllPaginated(dispatchFunc, key) {
        const allItems = [];
        let page = 1;

        while (true) {
            try {
                const result = (await dispatch(dispatchFunc({ page }))) as any;
                if (!result || result.length === 0) break;

                allItems.push(...result);
                page++;
            } catch (err: any) {
                // Catch error object here directly from the thunk
                if (err?.message?.includes('Invalid page')) {
                    console.warn(`[Silent] Reached end of ${key} pages.`);
                    dispatch(resetNotificationsErrors()); // Clear the error
                    break;
                } else {
                    console.warn(`Failed to fetch ${key}:`);
                    break;
                }
            }
        }

        return allItems;
    }

    function buildJobIndexes(jobs: any[]) {
        const byTask = new Map<number, any[]>();
        for (const j of jobs) {
            if (!byTask.has(j.taskId)) byTask.set(j.taskId, []);
            byTask.get(j.taskId)!.push(j);
        }

        const annJobByTaskId = new Map<number, any>();
        const lastQcJobByTaskId = new Map<number, any>();
        const qcCountByTaskId = new Map<number, number>();

        for (const [taskId, arr] of byTask) {
            annJobByTaskId.set(
                taskId,
                arr.find((j) => j.type === 'annotation'),
            );
            const qc = arr.filter((j) => j.type === 'quality_check');
            qcCountByTaskId.set(taskId, qc.length);
            if (qc.length) {
                lastQcJobByTaskId.set(
                    taskId,
                    qc.reduce((a, b) => (a.createdDate > b.createdDate ? a : b)),
                );
            }
        }

        return { annJobByTaskId, lastQcJobByTaskId, qcCountByTaskId };
    }

    useEffect(() => {
        (async () => {
            const allTasks = await fetchAllPaginated(getTasksAsyncArray, 'tasks');
            const allJobs = await fetchAllPaginated(getJobsAsyncArray, 'jobs');
            const allProjects = await fetchAllPaginated(getProjectsAsyncArray, 'projects');

            setFullTasks(allTasks);
            setFullJobs(allJobs);
            setFullProjects(allProjects);
        })();
    }, [dispatch]);

    useEffect(() => {
        async function fetchQualityCheckData() {
            if (!fullTasks.length || !fullProjects.length || !fullJobs.length) {
                console.log('Data not loaded yet. Waiting...');
                return;
            }
            console.log('Data loaded. Fetching comments...');

            const { annJobByTaskId, lastQcJobByTaskId, qcCountByTaskId } = buildJobIndexes(fullJobs);
            const projectById = new Map<number, any>(fullProjects.map((p) => [p.id, p]));

            // Fetch ALL saved QA rows (paginated)
            const qaEntries = await fetchAllPages<QualityAssessmentEntry>(
                '/api/quality_assessment?page=1&page_size=100',
            );

            // Composite key: dataset_name + annotation_created_date
            const apiByKey = new Map<string, string>();
            qaEntries.forEach((item) => {
                const key = `${item.dataset_name}::${item.annotation_created_date}`;
                apiByKey.set(key, item.comment || '');
            });

            const CONCURRENCY = 8; // This can be tuned if necessary, if the server is overhelmed lower this number

            const rows = await mapWithConcurrency(fullTasks, CONCURRENCY, async (task) => {
                const taskId = task.id;
                const datasetName = task.name;

                const projectId = task.projectId as number | undefined;
                const relatedProject = projectId ? projectById.get(projectId) || null : null;
                try{
                    const annotationJob = annJobByTaskId.get(taskId);
                    const lastQcJob = lastQcJobByTaskId.get(taskId) || null;
                    const qcJobCount = qcCountByTaskId.get(taskId) || 0;

                    let issueCount = 0,
                        classificationCount = 0,
                        omissionCount = 0,
                        outliningCount = 0,
                        extraAnnotationCount = 0,
                        otherIssuesCount = 0,
                        totalObjects = 0,
                        numImages = 0,
                        percentageIssues = 0;

                    if (annotationJob) {
                        try {
                            const response = await fetch(`/api/jobs/${annotationJob.id}/data/meta`, { credentials: 'include' });
                            const metaData = await response.json();

                            if ('stop_frame' in metaData) {
                                numImages = metaData.stop_frame + 1;
                            } else {
                                console.warn(`stop_frame missing in API response for annotation job ${annotationJob.id}`);
                            }
                        } catch (error) {
                            console.error(`Failed to fetch metadata for annotation job ${annotationJob.id}`, error);
                        }
                    }

                    let generalCommentsString = '';

                    if (lastQcJob) {
                        const [issuesRes, metaRes] = await Promise.allSettled([
                            lastQcJob.issues(),
                            fetch(`/api/jobs/${lastQcJob.id}/data/meta`, { credentials: 'include' }).then((r) => r.json()),
                        ]);

                        if (issuesRes.status === 'fulfilled') {
                            const issues = issuesRes.value;

                            const GENERAL_RE = /^general(?:comment|issue)#-/i;
                            const DESCRIPTION_RE = /description\s*:\s*(.*)/i;

                            for (const issue of issues) {
                                const msg = issue?.comments?.[0]?.message ?? '';

                                // General comments are not counted as issues
                                if (GENERAL_RE.test(msg)) {
                                    const cleaned = msg.replace(GENERAL_RE, '');
                                    generalCommentsString += `${(issue.frame ?? 0) + 1}#${cleaned}\n`;
                                    continue;
                                }

                                // contains "description:" add to general comments but still count as issue
                                const descMatch = msg.match(DESCRIPTION_RE);
                                if (descMatch) {
                                    const cleaned = descMatch[1].trim();
                                    generalCommentsString += `${(issue.frame ?? 0) + 1}#${cleaned}\n`;
                                }

                                // Classify and count issue
                                const lower = msg.toLowerCase();
                                if (lower.includes('classification')) classificationCount++;
                                else if (
                                    lower.includes('omission') ||
                                    lower.includes('nd') ||
                                    lower.includes('oubli') ||
                                    lower.includes('missing') ||
                                    lower.includes('not detected')
                                ) omissionCount++;
                                else if (lower.includes('outlining')) outliningCount++;
                                else if (lower.includes('extra annotation')) extraAnnotationCount++;
                                else otherIssuesCount++;

                                issueCount++;
                            }
                        } else {
                            console.error(`Failed to fetch issues for QC job ${lastQcJob.id}`, issuesRes.reason);
                        }

                        if (metaRes.status === 'fulfilled') {
                            const metaData = metaRes.value;

                            if ('object_count' in metaData) {
                                totalObjects = Number(metaData.object_count);
                            } else {
                                console.warn(`object_count missing in API response for job ${lastQcJob.id}`);
                            }

                            if ('exclude_count' in metaData) {
                                totalObjects -= Number(metaData.exclude_count);
                            } else {
                                console.warn(`exclude_count missing in API response for job ${lastQcJob.id}`);
                            }

                            if (totalObjects < 0) totalObjects = 0;
                        } else {
                            console.error(`Failed to fetch object count for QC job ${lastQcJob.id}`, metaRes.reason);
                        }

                        if (totalObjects > 0) {
                            percentageIssues = parseFloat(((issueCount / totalObjects) * 100).toFixed(2));
                        }
                    }

                    const annotationCreatedDate = annotationJob ? formatFR(annotationJob.createdDate) : 'N/A';

                    // Prefill comment ONLY if the saved comment belongs to this taskId
                    const commentKey = `${datasetName}::${annotationCreatedDate}`;
                    const { owner, text } = parseSig(apiByKey.get(commentKey));
                    const commentForThisTask = owner && owner === taskId ? text : '';

                    let stage = annotationJob ? annotationJob.stage : 'Unknown';
                    let state = 'Unknown';

                    if (annotationJob) {
                        if (stage === 'annotation') {
                            state = annotationJob.state;
                        } else if (stage === 'validation' || stage === 'acceptance') {
                            state = lastQcJob ? lastQcJob.state : annotationJob.state;
                        } else {
                            state = annotationJob.state;
                        }
                    }
                        return {
                            key: `task-${taskId}`,
                            taskId,
                            batchName: relatedProject ? relatedProject.name : '',
                            datasetName,
                            state,
                            stage,
                            issueCount,
                            classificationCount,
                            omissionCount,
                            outliningCount,
                            extraAnnotationCount,
                            otherIssuesCount,
                            totalObjects,
                            percentageIssues,
                            numImages,
                            annotationCreatedDate,
                            lastAnnotationUpdatedDate: annotationJob ? formatFR(annotationJob.updatedDate) : 'N/A',
                            qcJobCount,
                            comment: commentForThisTask,
                            generalComments: generalCommentsString || '',
                        } as DataType;
                    } catch (err) {
                        console.error(`QC row failed for task ${taskId}`, err);
                        // return a row with error state but still include batch/dataset info for filtering
                        return {
                            key: `task-${taskId}`,
                            taskId,
                            batchName: relatedProject ? relatedProject.name : '',
                            datasetName,
                            state: 'error',
                            stage: 'error',
                            issueCount: 0,
                            classificationCount: 0,
                            omissionCount: 0,
                            outliningCount: 0,
                            extraAnnotationCount: 0,
                            otherIssuesCount: 0,
                            totalObjects: 0,
                            percentageIssues: 0,
                            numImages: 0,
                            annotationCreatedDate: 'N/A',
                            lastAnnotationUpdatedDate: 'N/A',
                            qcJobCount: 0,
                            comment: '',
                            generalComments: `Error while loading row`,
                        } as DataType;
                    }
                });
            const formattedData = rows;

            // Build commentsById from computed rows
            const commentsById: Record<number, string> = {};
            for (const row of formattedData) {
                if (row.comment) commentsById[row.taskId] = row.comment;
            }

            setData(formattedData);
            setComments(commentsById);
            setLoading(false);

        }

        fetchQualityCheckData();
    }, [fullTasks, fullJobs, fullProjects]);
    
    const columns = [
        { title: 'Batch Name', dataIndex: 'batchName', key: 'batchName' },
        {
            title: 'Dataset Name',
            dataIndex: 'datasetName',
            key: 'datasetName',
            render: (_text: string, record: DataType) => {
                const task = fullTasks.find((t) => t.name === record.datasetName);
                if (!task) return record.datasetName;

                return (
                    <a href={`/tasks/${task.id}`} target='_blank' rel='noopener noreferrer' className='qa-dataset-link'>
                        {record.datasetName}
                    </a>
                );
            },
        },
        { title: 'Number of Images', dataIndex: 'numImages', key: 'numImages' },
        { title: 'State', dataIndex: 'state', key: 'state' },
        { title: 'Stage', dataIndex: 'stage', key: 'stage' },
        { title: 'Issues Found', dataIndex: 'issueCount', key: 'issueCount' },
        { title: 'Incorrect Classification', dataIndex: 'classificationCount', key: 'classificationCount' },
        { title: 'Omission Issues', dataIndex: 'omissionCount', key: 'omissionCount' },
        { title: 'Outlining Issues', dataIndex: 'outliningCount', key: 'outliningCount' },
        { title: 'Extra Annotations', dataIndex: 'extraAnnotationCount', key: 'extraAnnotationCount' },
        { title: 'Other Issues', dataIndex: 'otherIssuesCount', key: 'otherIssuesCount' },
        { title: 'Dataset Objects', dataIndex: 'totalObjects', key: 'totalObjects' },
        {
            title: 'Percentage Issues',
            dataIndex: 'percentageIssues',
            key: 'percentageIssues',
            render: (_: number, record: DataType) => {
                if (record.qcJobCount === 0) {
                    // If no  QC has been done display N/A
                    return <Tag color='default'>N/A</Tag>;
                }
                const color = record.percentageIssues > 5 ? 'red' : 'green';
                return <Tag color={color}>{record.percentageIssues.toFixed(2)}%</Tag>;
            },
        },
        { title: 'Annotation Job Created', dataIndex: 'annotationCreatedDate', key: 'annotationCreatedDate' },
        { title: 'Last Annotation Updated', dataIndex: 'lastAnnotationUpdatedDate', key: 'lastAnnotationUpdatedDate' },
        { title: 'Number of iteration', dataIndex: 'qcJobCount', key: 'qcJobCount' },
        {
            title: 'Comments',
            dataIndex: 'comment',
            key: 'comment',
            render: (_text: string, record: DataType) => (
                <Input.TextArea
                    value={comments[record.taskId] || ''}
                    onChange={(e) => handleCommentChange(record.taskId, e.target.value)}
                    onBlur={() => handleCommentBlur(record.taskId, record.datasetName)}
                    onKeyPress={(e) => handleCommentKeyPress(record.taskId, record.datasetName, e)}
                    placeholder='Enter your comment...'
                    autoSize={{ minRows: 2, maxRows: 5 }}
                    style={{ width: '100%' }}
                />
            ),
        },
        {
            title: 'Export',
            key: 'export',
            render: (_text: string, record: DataType) => {
                const task = fullTasks.find((t) => t.name === record.datasetName);
                const annotationJob = fullJobs.find((j) => j.taskId === task?.id && j.type === 'annotation');

                return (
                    <Button
                        type='primary'
                        icon={<DownloadOutlined />}
                        onClick={() => {
                            if (task) {
                                const taskInstance = new core.classes.Task(task);
                                dispatch(exportActions.openExportDatasetModal(taskInstance));
                            } else {
                                message.error('No task found for this dataset');
                            }
                        }}
                    >
                        Export
                    </Button>
                );
            },
        },
        {
            title: 'Delete',
            key: 'delete',
            render: (_text: string, record: DataType) => {
                const task = fullTasks.find((t) => t.name === record.datasetName);

                return (
                    <Button
                        danger
                        type='primary'
                        onClick={() => {
                            if (!task) {
                                message.error('Task not found');
                                return;
                            }

                            Modal.confirm({
                                title: `The task "${task.name}" will be deleted`,
                                content: 'All related data (images, annotations) will be lost. Continue?',
                                className: 'cvat-modal-confirm-delete-task',
                                onOk: async () => {
                                    const taskInstance = new core.classes.Task(task);
                                    await dispatch(deleteTaskAsync(taskInstance));
                                    message.success(`Task "${task.name}" deleted.`);
                                    refreshTable();
                                },
                                okButtonProps: {
                                    type: 'primary',
                                    danger: true,
                                },
                                okText: 'Delete',
                            });
                        }}
                    >
                        Delete
                    </Button>
                );
            },
        },
        {
            title: 'General Comments Summary',
            dataIndex: 'generalComments',
            key: 'generalComments',
            render: (text: string) => <div className='qa-general-comments'>{text}</div>,
        },
    ];

    const isFetching = loading;
    const filteredData = data.filter((d) => {
        const batchMatch = !selectedBatch || selectedBatch === '__all__' || d.batchName === selectedBatch;
        const datasetMatch = !selectedDataset || selectedDataset === '__all__' || d.datasetName === selectedDataset;
        return batchMatch && datasetMatch;
    });

    const uniqueBatches = Array.from(new Set(data.map((d) => d.batchName).filter((n) => n)));
    const uniqueDatasets = Array.from(
        new Set(
            data
                .filter((d) => !selectedBatch || selectedBatch === '__all__' || d.batchName === selectedBatch)
                .map((d) => d.datasetName)
                .filter((n) => n),
        ),
    );

    return (
        <>
            <div className='qa-toolbar'>
                <Select
                    showSearch
                    optionFilterProp='children'
                    filterOption={(input, option) =>
                        (option?.children as string)?.toLowerCase().startsWith(input.toLowerCase())
                    }
                    placeholder='Filter by batch'
                    allowClear
                    style={{ width: 250, marginRight: 8 }}
                    value={selectedBatch}
                    onChange={(value) => {
                        setSelectedBatch(value || null);
                        setSelectedDataset(null); // reset dataset when batch changes
                    }}
                >
                    <Select.Option key='__all__' value='__all__'>
                        All Batches
                    </Select.Option>
                    {uniqueBatches.map((name) => (
                        <Select.Option key={name} value={name}>
                            {name}
                        </Select.Option>
                    ))}
                </Select>
                <Select
                    showSearch
                    optionFilterProp='children'
                    filterOption={(input, option) =>
                        (option?.children as string)?.toLowerCase().startsWith(input.toLowerCase())
                    }
                    placeholder='Filter by dataset'
                    allowClear
                    style={{ width: 250, marginRight: 8 }}
                    value={selectedDataset}
                    onChange={(value) => setSelectedDataset(value || null)}
                >
                    <Select.Option key='__all__' value='__all__'>
                        All Datasets
                    </Select.Option>
                    {uniqueDatasets.map((name) => (
                        <Select.Option key={name} value={name}>
                            {name}
                        </Select.Option>
                    ))}
                </Select>
                <Button
                    className='qa-sync-btn'
                    onClick={sendAllToCropDataSync}
                >
                    Sync with ProdMonitoring
                </Button>

                <Button className='qa-export-btn' onClick={exportToCSV}>
                    Download Quality Control Table CSV
                </Button>
            </div>

            {isFetching ? (
                <div className='qa-loading'>
                    <Spin size='large' tip='Loading quality assessment table...' />
                </div>
            ) : (
                <>
                    <Table
                        columns={columns}
                        dataSource={filteredData}
                        rowKey='key'
                        pagination={{ pageSize: 50, showSizeChanger: false }}
                        locale={{ emptyText: 'No data found.' }}
                        rowClassName={(record: DataType) => {
                            const { state, stage } = record;

                            if (state === 'completed' && stage === 'annotation') return 'qa-annotation-completed';
                            if (state === 'completed' && stage === 'validation') return 'qa-review-completed';
                            if (state === 'rejected' && stage === 'acceptance') return 'qa-acceptance-rejected';
                            if (state === 'completed' && stage === 'acceptance') return 'qa-acceptance-completed';

                            return '';
                        }}
                    />

                    <ExportDatasetModal />
                </>
            )}
        </>
    );
}
